import { useEffect, useState } from 'react';
import { faCheck, faLockKeyhole } from '@fortawesome/pro-regular-svg-icons';
import { faEye, faFiles } from '@fortawesome/pro-solid-svg-icons';

import { ModalProps } from '@typings';
import { getSecret } from '@services';
import { clusterContextSelector, contextNamesSelector } from '@selectors';
import { useSelector } from '@hooks';
import { as, cn, invariant, toastifyResponseError } from '@utils';
import { noop } from '@utils/noop';

import {
  Button,
  ChaseSpinner,
  Field,
  Icon,
  Modal,
  Render,
} from '@components/Shared';
import { FieldSideAction } from '@components/Ui/FieldSideAction';

type Props = ModalProps & {
  secretKey: string;
};
export const RevealSecretModal: React.FC<Props> = ({
  secretKey,
  closeModal = noop,
}) => {
  const cluster = useSelector(clusterContextSelector);
  const { organizationName, projectName } = useSelector(contextNamesSelector);

  const { secretsUrl } = as.c(cluster);

  const [secret, setSecret] = useState('');
  const [isRevealed, setRevealed] = useState(false);
  const [isCopied, setCopied] = useState(false);

  const isLoading = isRevealed && !secret;

  const icon = (() => {
    if (isCopied) {
      return faCheck;
    }

    if (isRevealed) {
      return faFiles;
    }

    return faEye;
  })();

  useEffect(() => {
    const run = async () => {
      try {
        invariant(organizationName);
        invariant(projectName);

        const { value } = await getSecret({
          secretsUrl,
          organizationName,
          projectName,
          key: secretKey,
        });

        setSecret(value);
      } catch (error) {
        toastifyResponseError(error);
      }
    };

    run();
  }, [secretsUrl, organizationName, projectName, secretKey]);

  const handleAction = async () => {
    if (isRevealed && secret) {
      await navigator.clipboard.writeText(String(secret));

      setCopied(true);

      setTimeout(() => setCopied(false), 4000);
    }

    if (!isRevealed) {
      setRevealed(true);
    }
  };

  return (
    <Modal.Content title="Reveal Secret" className="w-[640px]">
      <div className="my-2">
        <p className="mb-4">This value is private. Never share it publicly</p>
        <FieldSideAction className="relative flex items-stretch rounded-lg p-0">
          <Field.Input
            label={secretKey}
            readOnly
            value={
              isRevealed && secret
                ? secret
                : '••••••••••••••••••••••••••••••••••••••••••••••••••••••••'
            }
            containerClassName="flex-1"
            className="pointer-events-none truncate border-none"
          >
            <div slot="icon" className="absolute left-3.5 top-5">
              <Icon icon={faLockKeyhole} className="text-neural-05" />
            </div>
          </Field.Input>
          <Button
            variant="ghost"
            className="h-auto rounded-l-none hover:bg-primary-subtle"
            onClick={handleAction}
          >
            <Render if={!isLoading}>
              <Icon
                icon={icon}
                className={cn('text-[20px] text-neural-04', {
                  'text-success': isCopied,
                })}
              />
            </Render>
            <Render if={isLoading}>
              <ChaseSpinner color="primary" className="h-5 w-5" />
            </Render>
          </Button>
        </FieldSideAction>
      </div>
      <Modal.Footer>
        <Button variant="secondary" onClick={closeModal}>
          Close
        </Button>
      </Modal.Footer>
    </Modal.Content>
  );
};
